import {
  getLeoUuid,
  LeoUUID,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { BackOfficeSurveySettings, BackOfficeSurveySettingsDTO } from "./backOfficeSurveySettings";
/* eslint-disable import/export */
export abstract class ReOpenSurveyRPC implements
  LeoRPC<ReOpenSurveyRPC.Request, ReOpenSurveyRPC.Response, ReOpenSurveyRPC.Errors.Errors>
{
  abstract execute(
    request: ReOpenSurveyRPC.Request
  ): Promise<LeoRPCResult<ReOpenSurveyRPC.Response, ReOpenSurveyRPC.Errors.Errors>>;
}

export namespace ReOpenSurveyRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    surveyId: string;
    projectId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isReOpenSurveyRPCRequest: boolean = true;
  
    readonly surveyId: LeoUUID;
    readonly projectId: LeoUUID;
  
  
    constructor(
    surveyId: LeoUUID, 
    projectId: LeoUUID
    ) {
      this.surveyId = surveyId;
      this.projectId = projectId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      return new Request(
        surveyId,
        projectId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          surveyId: this.surveyId.toDTO(),
          projectId: this.projectId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `ReOpenSurveyRPC(surveyId='${this.surveyId}',projectId='${this.projectId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    backOfficeSurveySettings: BackOfficeSurveySettingsDTO;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isReOpenSurveyResponse: boolean = true;
  
    readonly backOfficeSurveySettings: BackOfficeSurveySettings;
  
  
    constructor(
    backOfficeSurveySettings: BackOfficeSurveySettings
    ) {
      this.backOfficeSurveySettings = backOfficeSurveySettings;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const backOfficeSurveySettings = BackOfficeSurveySettings.fromDTO(getObject(dto, "backOfficeSurveySettings", InvalidResponseBackOfficeSurveySettingsError));
      return new Response(
        backOfficeSurveySettings
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          backOfficeSurveySettings: this.backOfficeSurveySettings.toDTO()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `ReOpenSurvey(backOfficeSurveySettings='${this.backOfficeSurveySettings}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    InvalidSurveyId = "INVALID_SURVEY_ID",
    SurveyIsNotClosed = "SURVEY_IS_NOT_CLOSED",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `ReOpenSurveyRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `ReOpenSurveyRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface SurveyIsNotClosedDTO {
      code: string;
    }
    
    export class SurveyIsNotClosed extends LeoRPCError<RPCError> {
      readonly isSurveyIsNotClosed: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SurveyIsNotClosed;
        super(code);
      }
    
    
      toDTO(): SurveyIsNotClosedDTO {
        const dto: SurveyIsNotClosedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SurveyIsNotClosed.prototype.toString = function toString(): string {
      return `ReOpenSurveyRPC.SURVEY_IS_NOT_CLOSED()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `ReOpenSurveyRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    

    export type Errors = InvalidProjectId | InvalidSurveyId | SurveyIsNotClosed | ProjectAlreadyArchived;
    export type ErrorsDTO = InvalidProjectIdDTO | InvalidSurveyIdDTO | SurveyIsNotClosedDTO | ProjectAlreadyArchivedDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.SurveyIsNotClosed:
          return new SurveyIsNotClosed();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSurveyIdError extends ReOpenSurveyRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestProjectIdError extends ReOpenSurveyRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidResponseBackOfficeSurveySettingsError extends ReOpenSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseBackOfficeSurveySettingsError";
  }
}

export class InvalidResponseInvalidProjectIdError extends ReOpenSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends ReOpenSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseSurveyIsNotClosedError extends ReOpenSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsNotClosedError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends ReOpenSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

